import React, { CSSProperties, useEffect, useRef, useState } from "react";
import "./Modal.css";
import { createPortal } from "react-dom";
import Separator from "../ui/Separator";
import FocusTrap from "focus-trap-react";

type ModalVariant = "info" | "warning" | "delete" | "confirmation" | "default";

interface Props {
  open: boolean;
  escFn?: () => void;
  closeFn?: () => void;
  closeIcon?: string | React.ReactNode;
  content?: React.ReactNode;
  titleContent?: React.ReactNode;
  actions?: React.ReactElement; // This will be used to pass buttons or other actions as children
  style?: CSSProperties;
  variant?: ModalVariant;
}

export const Modal = ({
  open,
  closeIcon,
  titleContent,
  content,
  actions,
  escFn,
  style,
  variant = "default",
}: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);

  const checkModalHeight = () => {
    if (modalRef.current) {
      const shouldScroll = modalRef.current.scrollHeight > window.innerHeight;
      if (shouldScroll !== isScrollable) {
        setIsScrollable(shouldScroll);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        escFn?.();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      if (typeof window != "undefined" && window.document) {
        document.body.style.overflow = "hidden"; // Prevent background scroll
        checkModalHeight(); // Check if content exceeds screen height

        // Use MutationObserver to watch for changes inside the modal
        observerRef.current = new MutationObserver(() => {
          checkModalHeight();
        });

        if (modalRef.current) {
          observerRef.current.observe(modalRef.current, {
            childList: true,
            subtree: true,
          });
        }
      }
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset"; // Reset when modal closes
      document.removeEventListener("keydown", handleKeyDown);
      observerRef.current?.disconnect();
      setIsScrollable(false);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      checkModalHeight();
    }
  }, [content]);

  if (!open) return null;

  return createPortal(
    <FocusTrap>
      <div className={`overlay ${isScrollable ? "scrollable" : ""}`}>
        <div
          className={"modal"}
          style={style}
          id="modal-dialog"
          data-testid="modal-dialog"
          ref={modalRef}
        >
          <div className={"modal__container"}>
            <div className={"modal__heading__container"}>
              <div className={"modal__heading"}>
                {titleContent && (
                  <div
                    className={`modal__heading variant--${variant || "default"}`}
                  >
                    {titleContent}
                    {closeIcon}
                  </div>
                )}
              </div>
              <Separator />
            </div>
            <div>{content}</div>
            <div style={{ marginTop: "2rem" }}>{actions && actions}</div>
          </div>
        </div>
      </div>
    </FocusTrap>,
    document.body,
  );
};
